import React, {useEffect} from 'react';

import {useStore} from 'effector-react';
import {Controller, Control} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import {getProducts} from 'api/orders';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Select} from 'components/ui-kit/Select';
import {products$, firstStep$} from 'models/orders/create';
import {GtinOrderListItem, PackageTypes, ProductStatuses} from 'types/gtins';

import {ProductGroupIconContainer} from '../styled';

import {GtinOptionComponent} from './gtinOptionComponent';

interface FormItemProps {
    control: Control;
    defaultValue: string;
    name: string;
    fieldName?: string;
    getValues: Function;
}

export const PAGE_SIZE = 35;
const SEARCH_DEBOUNCE_MS = 500;

export const ProductCode = ({control, getValues, fieldName, ...props}: FormItemProps) => {
    const products = useStore(products$);
    const {t} = useTranslation();
    const state = useStore(firstStep$);
    const [gtins, setGtins] = React.useState<GtinOrderListItem[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const requestIdRef = React.useRef(0);
    const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const loadGtins = React.useCallback(async (search: string) => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        try {
            const response = await getProducts({
                paging: {
                    pageNumber: 0,
                    pageSize: PAGE_SIZE,
                },
                filters: [
                    {
                        id: 'status',
                        value: [ProductStatuses.PUBLISHED],
                    },
                    ...(state.productTypes && state.productTypes === 'import'
                        ? [
                              {
                                  id: 'productType',
                                  value: ['GTIN'],
                              },
                          ]
                        : []),
                    ...(search ? [{id: 'gtin', value: [search]}] : []),
                ],
                viewMode: 'MY',
            });

            if (requestId !== requestIdRef.current) return;

            if (response.length === 0) {
                setGtins([]);
                toast.error(t('pages.orders.form.noProductsFound'));
                return;
            }

            setGtins(response);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        setGtins([]);
        loadGtins('');
    }, [state.factoryId, loadGtins]);

    useEffect(
        () => () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        },
        [],
    );

    const handleInputChange = React.useCallback(
        (input: string, {action}: {action: string}) => {
            if (action !== 'input-change') return;

            setSearchQuery(input);

            if (input.length > 0 && input.length < 4) return;

            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                loadGtins(input);
            }, SEARCH_DEBOUNCE_MS);
        },
        [loadGtins],
    );

    const gtinOptions = React.useMemo(() => {
        if (!gtins.length) return [];
        return gtins.map((val: GtinOrderListItem) => ({
            value: val.gtin,
            label: val.gtin,
            meta: {
                productName: val.productName ?? '',
                productGroupCode: val.productGroupCode ?? '',
                type: val.packageType ?? '',
                productVersionId: val.productId ?? '',
            },
        }));
    }, [gtins]);

    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}, {invalid}) => {
                    const currentProduct = products.find((item) => item.gtin === value);
                    const productGroupCode =
                        currentProduct?.productGroupCode ||
                        getValues(`${fieldName}.productGroupCode`);
                    return (
                        <div style={{position: 'relative'}}>
                            {!!productGroupCode && (
                                <ProductGroupIconContainer>
                                    <ProductGroupIcon value={productGroupCode} />
                                </ProductGroupIconContainer>
                            )}
                            <Select
                                name={`gtin`}
                                label={t('pages.orders.form.selectProduct')}
                                placeholder={t('pages.orders.form.selectProductPlaceholder')}
                                isClearable
                                optionComponent={GtinOptionComponent}
                                value={value}
                                onChange={(val: {
                                    value: string;
                                    meta: {
                                        productName: string;
                                        productGroupCode: string;
                                        type: keyof typeof PackageTypes;
                                        productVersionId: string;
                                    };
                                }) => {
                                    onChange(val);
                                    if (debounceTimerRef.current) {
                                        clearTimeout(debounceTimerRef.current);
                                    }
                                    setSearchQuery('');
                                    loadGtins('');
                                }}
                                options={gtinOptions}
                                hasError={invalid}
                                isLoading={isLoading}
                                inputValue={searchQuery}
                                onInputChange={handleInputChange}
                                filterOption={() => true}
                            />
                        </div>
                    );
                }}
            />
        </>
    );
};
